import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, RotateCcw, X } from 'lucide-react';
import { ImageCropCanvas, useCropState } from '../ImageCropModal';
import { isAvifFile, isHeicFile, normalizeImageForUpload } from '../../utils/heicConvert';
import { esArchivoDeFuente, tituloDeElemento } from '../../pages/v2/elementosDeMarcaApi';
import { esZip, leerFuentesDeZip } from '../../utils/fuentesDeUnZip';

/**
 * La pantalla que se abre al subir una imagen a Elementos de marca, antes de guardarla:
 * recortarla y ponerle título — ADR-2026-10-20-el-elemento-se-prepara-antes-de-subir.
 *
 * Un logo o un gráfico casi nunca llega listo: viene con aire alrededor, y así se pega en
 * la pieza con ese aire. El recorte es el del visor (`useCropState`), con zoom sutil al
 * soltar y el lienzo a la densidad de la pantalla. Si no se toca nada, se sube el archivo
 * original, como antes. Quitar el fondo estuvo acá y se sacó
 * (ADR-2026-10-20-la-preparacion-del-elemento-no-quita-el-fondo).
 *
 * Las tipografías no pasan por acá: no hay nada que recortar.
 */

const extDe = (file: File) => file.name.match(/\.([a-z0-9]{1,6})$/i)?.[1]?.toLowerCase() ?? 'png';

/** El archivo que se guarda: el original si no se tocó (sólo con el título como nombre),
 *  o el PNG del recorte. */
export function archivoFinal(original: File, titulo: string, editado: Blob | null): File {
  const base = titulo.trim() || tituloDeElemento(original.name, 'element');
  if (!editado) return new File([original], `${base}.${extDe(original)}`, { type: original.type });
  return new File([editado], `${base}.png`, { type: 'image/png' });
}

type Props = {
  archivo: File;
  /** Posición en la tanda (1 de 3). */
  posicion: number;
  total: number;
  onGuardar: (file: File) => void;
  onDescartar: () => void;
  onCerrar: () => void;
};

/** Lo que el navegador puede dibujar: HEIC y AVIF se convierten antes, como al subir. */
function useUrlDibujable(archivo: File) {
  const [estado, setEstado] = useState<{ url: string } | { error: string } | null>(null);
  useEffect(() => {
    let vivo = true;
    let url: string | null = null;
    (async () => {
      try {
        const blob = isHeicFile(archivo) || isAvifFile(archivo) ? await normalizeImageForUpload(archivo) : archivo;
        url = URL.createObjectURL(blob);
        if (vivo) setEstado({ url });
        else URL.revokeObjectURL(url);
      } catch (e) {
        if (vivo) setEstado({ error: e instanceof Error && e.message ? e.message : 'No pudimos abrir esa imagen.' });
      }
    })();
    return () => {
      vivo = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [archivo]);
  return estado;
}

const Marco = ({ children, onCerrar, bloqueado }: { children: ReactNode; onCerrar: () => void; bloqueado: boolean }) => {
  // Escape cierra ESTA pantalla y no la ventana de abajo (el selector "Elegí qué agregar"
  // también escucha la tecla): captura en window y no la deja seguir.
  useEffect(() => {
    const alaTecla = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      if (!bloqueado) onCerrar();
    };
    window.addEventListener('keydown', alaTecla, true);
    return () => window.removeEventListener('keydown', alaTecla, true);
  }, [onCerrar, bloqueado]);
  return createPortal(
    // Los eventos de React cruzan el portal hacia el árbol que la montó: sin cortarlos, un
    // click acá cerraría el selector de abajo.
    <div
      className="fixed inset-0 z-[150] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Preparar el elemento"
        className="flex max-h-[100dvh] w-full max-w-2xl flex-col overflow-hidden rounded-t-3xl bg-white sm:max-h-[92vh] sm:rounded-3xl"
      >
        {children}
      </div>
    </div>,
    document.body,
  );
};

export function PrepararElemento({ archivo, posicion, total, onGuardar, onDescartar, onCerrar }: Props) {
  const dibujable = useUrlDibujable(archivo);
  const [error, setError] = useState<string | null>(null);
  const [titulo, setTitulo] = useState(() => tituloDeElemento(archivo.name, 'element'));
  const [guardando, setGuardando] = useState(false);

  const imagen = dibujable && 'url' in dibujable ? dibujable.url : '';
  const recorte = useCropState(imagen, archivo.name, () => undefined, {
    margen: 16,
    conservarRecorte: true,
    zoomAlSoltar: true,
    nitido: true,
  });

  const guardar = async () => {
    if (guardando) return;
    setGuardando(true);
    try {
      const editado = recorte.recorteEntero ? null : await recorte.recortarABlob();
      if (!recorte.recorteEntero && !editado) {
        setError('No pudimos preparar la imagen. Probá de nuevo.');
        return;
      }
      onGuardar(archivoFinal(archivo, titulo, editado));
    } finally {
      setGuardando(false);
    }
  };

  const cerrar = useCallback(() => onCerrar(), [onCerrar]);
  const listo = recorte.imageLoaded && !!imagen;

  return (
    <Marco onCerrar={cerrar} bloqueado={guardando}>
      <div className="flex items-center gap-3 px-5 pb-3 pt-5">
        <div className="min-w-0 flex-1">
          <h2 className="text-[16px] font-semibold text-brand-black">Preparar el elemento</h2>
          <p className="mt-0.5 text-[13px] text-[#02062B80]">
            Arrastrá los bordes para recortarlo{total > 1 ? ` · ${posicion} de ${total}` : ''}
          </p>
        </div>
        <button
          type="button"
          onClick={cerrar}
          aria-label="Cerrar sin guardar"
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[#02062BB3] transition-colors hover:bg-black/[0.05]"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="relative mx-5 h-[min(52vh,440px)] overflow-hidden rounded-2xl">
        {dibujable && 'error' in dibujable ? (
          <div className="flex h-full items-center justify-center bg-[#F2F2F7] px-6 text-center text-[14px] text-red-600">
            {dibujable.error}
          </div>
        ) : imagen ? (
          <ImageCropCanvas cropState={recorte} />
        ) : (
          <div className="flex h-full items-center justify-center bg-[#F2F2F7]">
            <Loader2 className="h-6 w-6 animate-spin text-[#02062B4D]" />
          </div>
        )}
      </div>

      <div className="flex flex-col gap-4 overflow-y-auto px-5 pb-5 pt-4">
        <div className="flex flex-wrap items-center gap-2">
          {!recorte.recorteEntero && listo && (
            <button
              type="button"
              onClick={recorte.handleReset}
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-[13px] font-medium text-[#02062B99] transition-colors hover:bg-black/[0.04] hover:text-brand-black"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Imagen entera
            </button>
          )}
          {listo && (
            <span className="ml-auto text-[12.5px] text-[#02062B66]">
              {recorte.realWidth} × {recorte.realHeight} px
            </span>
          )}
        </div>

        {error && <p className="text-[13px] text-red-600">{error}</p>}

        <label className="flex flex-col gap-1.5">
          <span className="text-[12.5px] font-medium text-[#02062B99]">Título</span>
          <input
            value={titulo}
            maxLength={80}
            onChange={(e) => setTitulo(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void guardar();
            }}
            className="rounded-xl border border-black/[0.1] px-3.5 py-2.5 text-[14px] text-brand-black outline-none focus:border-brand-primary"
          />
        </label>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onDescartar}
            disabled={guardando}
            className="flex-1 rounded-xl border border-neutral-200 px-5 py-2.5 text-[14px] text-brand-black transition-colors hover:bg-neutral-50 disabled:opacity-40"
          >
            {total > 1 ? 'Saltear esta' : 'Cancelar'}
          </button>
          <button
            type="button"
            onClick={() => void guardar()}
            disabled={!listo || guardando}
            className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-brand-primary px-5 py-2.5 text-[14px] font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-40"
          >
            {guardando && <Loader2 className="h-4 w-4 animate-spin" />}
            Guardar
          </button>
        </div>
      </div>
    </Marco>
  );
}

/**
 * Lo que usan las dos puertas de subida (Mi marca y el selector "Elegí qué agregar"):
 * `preparar(archivos)` sube las tipografías directo y pone las imágenes en fila; `pantalla`
 * es la preparación de la primera de la fila, o null. Cada imagen lista sale por `subir`.
 * Un .zip se abre y sus tipografías suben como si se hubieran elegido sueltas
 * (ADR-2026-10-20-un-zip-de-tipografias-se-sube-entero); lo que no se pudo, por `avisar`.
 */
export function usePrepararElementos(subir: (files: File[]) => void, avisar?: (mensaje: string) => void) {
  const [fila, setFila] = useState<{ id: number; file: File }[]>([]);
  const [hechas, setHechas] = useState(0);
  const proximoId = useRef(0);

  const preparar = useCallback(
    async (files: File[]) => {
      const fuentes = files.filter(esArchivoDeFuente);
      const imagenes = files.filter((f) => !esArchivoDeFuente(f) && !esZip(f));
      const avisos: string[] = [];
      for (const zip of files.filter(esZip)) {
        try {
          const { fuentes: deZip, omitidas } = await leerFuentesDeZip(zip);
          fuentes.push(...deZip);
          if (omitidas > 0) {
            avisos.push(
              `De «${zip.name}» quedaron afuera ${omitidas} ${omitidas === 1 ? 'tipografía' : 'tipografías'} (muy pesadas o más de las que entran de una vez).`,
            );
          }
        } catch (e) {
          avisos.push(e instanceof Error && e.message ? e.message : `No pudimos abrir «${zip.name}».`);
        }
      }
      if (fuentes.length) subir(fuentes);
      // Después de subir: las dos puertas limpian su aviso al empezar una subida.
      if (avisos.length) avisar?.(avisos.join(' '));
      if (!imagenes.length) return;
      setFila((previa) => {
        if (previa.length === 0) setHechas(0);
        return [...previa, ...imagenes.map((file) => ({ id: proximoId.current++, file }))];
      });
    },
    [subir, avisar],
  );

  const avanzar = () => {
    setFila((previa) => previa.slice(1));
    setHechas((n) => n + 1);
  };
  const primera = fila[0];
  const pantalla =
    primera ? (
      <PrepararElemento
        key={primera.id}
        archivo={primera.file}
        posicion={hechas + 1}
        total={hechas + fila.length}
        onGuardar={(file) => {
          subir([file]);
          avanzar();
        }}
        onDescartar={avanzar}
        onCerrar={() => {
          setFila([]);
          setHechas(0);
        }}
      />
    ) : null;

  return { preparar, pantalla };
}

export default PrepararElemento;
